/**
 * @module app/App
 * @description Root component: providers, site chrome, route → page, and
 * the global overlays (Quick View, cart drawer, cart bar, toast).
 *
 * The tier decides the shell (config/tier.ts). Standard and Enhanced share
 * the browse shell; Enhanced differs only through FEATURES flags read inside
 * components, so no Standard code is forked.
 */
import type { JSX } from "react";
import { CartBar, CartDrawer, Toast } from "../components/Cart";
import { QuickView } from "../components/QuickView";
import { DemoNotice, SiteFooter, SiteHeader } from "../components/SiteChrome";
import { FEATURES } from "../config/tier";
import { BrowsePage } from "../pages/BrowsePage";
import { CheckoutPage } from "../pages/CheckoutPage";
import { PaymentPage } from "../pages/PaymentPage";
import { ThankYouPage } from "../pages/ThankYouPage";
import { GivingProvider } from "./GivingContext";
import { useRoute, type Route } from "./router";

/** Route → page for the browse shell. */
function BrowseShellPage({ route }: { route: Route }): JSX.Element {
    switch (route) {
        case "checkout": return <CheckoutPage />;
        case "payment": return <PaymentPage />;
        case "thank-you": return <ThankYouPage />;
        case "browse": return <BrowsePage />;
    }
}

/** The app. */
export function App(): JSX.Element {
    const route: Route = useRoute();

    if (FEATURES.shell === "stages") {
        // // TEMPORARY: Immersive (Send Love Live) shell lands in build step 3.
        //   What caused this: build order is Standard → Enhanced → Immersive.
        //   Why we chose it: keeps the immersive build compiling meanwhile.
        //   Side-effects: none; replaced by <StagesApp /> before deploy.
        return <main className="container section"><h1>Send Love Live: coming next</h1></main>;
    }

    return (
        <GivingProvider>
            <DemoNotice />
            <SiteHeader />
            <div className={`route route--${route}`} key={route}>
                <BrowseShellPage route={route} />
            </div>
            <SiteFooter />
            <QuickView />
            <CartDrawer />
            <CartBar />
            <Toast />
        </GivingProvider>
    );
}
